/**
 * Inline SVG icon set. Hand-rolled instead of pulling lucide because TMA bundles
 * are size-sensitive and we only need ~15 glyphs.
 *
 * All icons are 24×24, currentColor, stroke 1.75. Pass `size` to override.
 */

import type { SVGProps } from 'react';

export type IconName =
  | 'home' | 'users' | 'inbox' | 'settings'
  | 'chevron-right' | 'chevron-left' | 'plus'
  | 'play' | 'pause' | 'check' | 'x' | 'refresh' | 'sparkles'
  | 'shield' | 'message' | 'download' | 'upload' | 'flag' | 'dot' | 'search';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'children'> {
  name: IconName;
  size?: number | string;
}

const PATHS: Record<IconName, JSX.Element> = {
  'home':         <path d="M4 11l8-7 8 7v9a1 1 0 0 1-1 1h-4v-6h-6v6H5a1 1 0 0 1-1-1z" />,
  'users':        <><circle cx="9" cy="8" r="3.5"/><path d="M2.5 19c.6-3 3.4-5 6.5-5s5.9 2 6.5 5"/><circle cx="17" cy="9" r="2.5"/><path d="M15.5 14.5c2.4 0 4.4 1.6 5 4"/></>,
  'inbox':        <><path d="M3 12l3-7h12l3 7"/><path d="M3 12v6a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1v-6h-6l-2 2h-4l-2-2H3z"/></>,
  'settings':     <><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3 1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z"/></>,
  'chevron-right':<path d="M9 6l6 6-6 6"/>,
  'chevron-left': <path d="M15 6l-6 6 6 6"/>,
  'plus':         <><path d="M12 5v14"/><path d="M5 12h14"/></>,
  'play':         <path d="M7 5l12 7-12 7z"/>,
  'pause':        <><rect x="6" y="5" width="4" height="14" rx="1"/><rect x="14" y="5" width="4" height="14" rx="1"/></>,
  'check':        <path d="M5 12l5 5 9-11"/>,
  'x':            <><path d="M6 6l12 12"/><path d="M18 6L6 18"/></>,
  'refresh':      <><path d="M3 12a9 9 0 0 1 15-6.7L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 0 1-15 6.7L3 16"/><path d="M3 21v-5h5"/></>,
  'sparkles':     <><path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6z"/><path d="M19 14l.7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/></>,
  'shield':       <path d="M12 3l8 3v6c0 5-3.5 8.5-8 9-4.5-.5-8-4-8-9V6z"/>,
  'message':      <path d="M21 12a8 8 0 1 1-3.4-6.5L21 4l-1.5 4A8 8 0 0 1 21 12z"/>,
  'download':     <><path d="M12 4v12"/><path d="M7 11l5 5 5-5"/><path d="M5 20h14"/></>,
  'upload':       <><path d="M12 20V8"/><path d="M7 13l5-5 5 5"/><path d="M5 4h14"/></>,
  'flag':         <><path d="M5 21V4"/><path d="M5 4h11l-2 4 2 4H5"/></>,
  'dot':          <circle cx="12" cy="12" r="4"/>,
  'search':       <><circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/></>,
};

export function Icon({ name, size = 20, ...rest }: IconProps): JSX.Element {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      {...rest}
    >
      {PATHS[name]}
    </svg>
  );
}
